import { useState } from 'react'
import { Dialog } from 'radix-ui'
import { X } from 'lucide-react'
import { useCreateIntegrationSource } from '../hooks'
import type { AuthType, CreateIntegrationSourceRequest } from '../types'
import { SourceConfigFields } from './SourceConfigFields'
import { emptySourceConfigDraft, buildSourceConfig, type SourceConfigDraft } from './sourceConfigDraft'

type Props = {
  onClose: () => void
}

const CODE_REGEX = /^[a-z][a-z0-9_]*$/
const AUTH_TYPES: AuthType[] = ['API_KEY', 'HMAC', 'JWT']

/**
 * Tworzenie nowego źródła integracji. Tożsamość (kod/typ auth) + nazwa + flaga aktywności
 * tutaj; reszta configu (auth, encja docelowa, intake contract, CORS) w SourceConfigFields.
 */
export function AddSourceDialog({ onClose }: Props) {
  const create = useCreateIntegrationSource()
  const [code, setCode] = useState('')
  const [name, setName] = useState('')
  const [authType, setAuthType] = useState<AuthType>('API_KEY')
  const [isActive, setIsActive] = useState(true)
  const [draft, setDraft] = useState<SourceConfigDraft>(emptySourceConfigDraft())
  const [error, setError] = useState<string | null>(null)

  const submit = () => {
    setError(null)
    if (!CODE_REGEX.test(code)) {
      setError('Kod: małe litery, cyfry, podkreślenia. Musi zaczynać się od litery (np. acx_pompa).')
      return
    }
    if (!name.trim()) {
      setError('Nazwa nie może być pusta.')
      return
    }

    const { config, error: cfgError } = buildSourceConfig(draft)
    if (cfgError) {
      setError(cfgError)
      return
    }

    const body: CreateIntegrationSourceRequest = { code, name, authType, isActive, config }
    create.mutate(body, { onSuccess: onClose, onError: e => setError(formatError(e)) })
  }

  const labelCls = 'text-xs font-medium uppercase tracking-[0.06em] text-slate-600'
  const inputCls = 'w-full rounded border border-slate-300 px-2 py-1 text-sm'

  return (
    <Dialog.Root open onOpenChange={o => { if (!o) onClose() }}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-40 bg-slate-950/40" />
        <Dialog.Content className="fixed left-1/2 top-1/2 z-50 max-h-[90vh] w-full max-w-lg -translate-x-1/2 -translate-y-1/2 overflow-y-auto rounded bg-white p-5 shadow-xl">
          <div className="mb-4 flex items-start justify-between gap-3">
            <Dialog.Title className="text-base font-semibold">Nowe źródło integracji</Dialog.Title>
            <button onClick={onClose} className="text-slate-400 hover:text-slate-700" aria-label="Zamknij">
              <X className="h-4 w-4" />
            </button>
          </div>

          <div className="grid gap-3">
            <div className="grid grid-cols-2 gap-3">
              <label className="grid gap-1">
                <span className={labelCls}>Kod</span>
                <input
                  value={code}
                  onChange={e => setCode(e.target.value)}
                  disabled={create.isPending}
                  className={`${inputCls} font-mono`}
                  placeholder="acx_pompa"
                />
              </label>
              <label className="grid gap-1">
                <span className={labelCls}>Typ uwierzytelniania</span>
                <select
                  value={authType}
                  onChange={e => setAuthType(e.target.value as AuthType)}
                  disabled={create.isPending}
                  className={inputCls}
                >
                  {AUTH_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                </select>
              </label>
            </div>

            <label className="grid gap-1">
              <span className={labelCls}>Nazwa</span>
              <input
                value={name}
                onChange={e => setName(e.target.value)}
                disabled={create.isPending}
                className={inputCls}
                placeholder="ACX - pompy ciepła"
              />
            </label>

            <SourceConfigFields value={draft} onChange={setDraft} disabled={create.isPending} />

            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={isActive}
                onChange={e => setIsActive(e.target.checked)}
                disabled={create.isPending}
                className="size-4"
              />
              <span className="text-sm">Aktywne (POST events działa od razu)</span>
            </label>

            {error && <p className="text-xs text-red-600">{error}</p>}
          </div>

          <div className="mt-5 flex justify-end gap-2">
            <button
              type="button"
              onClick={onClose}
              disabled={create.isPending}
              className="rounded border border-slate-300 px-3 py-1.5 text-sm hover:bg-slate-50"
            >
              Anuluj
            </button>
            <button
              type="button"
              onClick={submit}
              disabled={create.isPending}
              className="rounded bg-sky-600 px-3 py-1.5 text-sm text-white hover:bg-sky-700 disabled:opacity-50"
            >
              {create.isPending ? 'Tworzenie...' : 'Utwórz'}
            </button>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  )
}

function formatError(e: unknown): string {
  if (e && typeof e === 'object' && 'response' in e) {
    const r = (e as { response: { data?: { title?: string; detail?: string } } }).response
    return r?.data?.detail ?? r?.data?.title ?? 'Błąd zapisu'
  }
  return 'Nieznany błąd'
}
